import api from './api'
import type { AmazonReportUploadResponse } from './documents'
import type { Document } from '@/types/document'

export type BackgroundJobType =
  | 'bulk_approve'
  | 'bulk_upload'
  | 'bulk_email_process'
  | 'bulk_reprocess'
  | 'bulk_allocate_shared_costs'
  | 'bulk_refresh_categories'
  | 'qbo_transaction_search'
  | 'qbo_transaction_apply'
  | 'gcp_cost_analysis'
  | 'gcp_cost_recommendations'
  | 'purchase_enrichment'
  | 'clover_backfill_audit'
  | 'clover_backfill_apply'
  | 'monthly_performance'

/** Job types whose list updates are merged in DocReview (skip full-list refresh on complete). */
export const MERGE_HANDLED_JOB_TYPES: ReadonlySet<BackgroundJobType> = new Set([
  'bulk_upload',
  'bulk_reprocess',
  'bulk_allocate_shared_costs',
  'bulk_refresh_categories',
])

export type BulkUploadSkipReason = 'content_hash' | 'header_key'

export interface BulkUploadSkippedDoc {
  filename: string
  reference_number: string | null
  skip_reason: BulkUploadSkipReason
  existing_document_id: number
  vendor_name: string | null
  existing_status: string
  existing_deleted: boolean
}

export type BackgroundJobStatus =
  | 'pending'
  | 'running'
  | 'waiting_for_clover_tokens'
  | 'paused_token_timeout'
  | 'completed'
  | 'completed_with_errors'
  | 'cancelled'
  | 'failed'

/** True while the job may still make progress (including waiting on fresh tokens). */
export function isBackgroundJobActive(status: BackgroundJobStatus): boolean {
  return (
    status === 'pending' ||
    status === 'running' ||
    status === 'waiting_for_clover_tokens'
  )
}

/** True when the job will not receive further progress updates. */
export function isBackgroundJobTerminal(status: BackgroundJobStatus): boolean {
  return (
    status === 'completed' ||
    status === 'completed_with_errors' ||
    status === 'failed' ||
    status === 'cancelled'
  )
}

export interface BackgroundJob {
  id: number
  job_type: BackgroundJobType
  status: BackgroundJobStatus
  total_items: number
  processed_items: number
  successful_items: number
  failed_items: number
  input_data?: Record<string, unknown>
  result_data?: {
    approved_docs?: { id: number; reference_number: string | null }[]
    uploaded_docs?: { id: number; filename: string; vendor_name: string | null }[]
    reprocessed_docs?: unknown[]
    updated_docs?: unknown[]
    processed_emails?: { gmail_id: string; inbox: string }[]
    skipped_emails?: { gmail_id: string; inbox: string }[]
    failed_docs?: { id?: number; filename?: string; reference_number?: string | null; error: string }[]
    skipped_docs?: BulkUploadSkippedDoc[]
    kind?: 'amazon_report'
    summary?: AmazonReportUploadResponse['summary']
    failed_emails?: { gmail_id: string; inbox: string; error: string }[]
    unprocessed_filenames?: string[]
    unprocessed_emails?: { gmail_id: string; inbox: string }[]
    unprocessed_document_ids?: number[]
  }
  error_message?: string
  created_at: string
  started_at?: string
  completed_at?: string
}

/** True when the job failed or completed with per-item / result errors worth surfacing in the UI. */
export function backgroundJobHasErrors(job: BackgroundJob): boolean {
  if (job.status === 'failed') return true
  if (job.status === 'completed_with_errors') return true
  if (job.error_message) return true
  if (job.failed_items > 0) return true
  const rd = job.result_data
  if (!rd) return false
  if ((rd.failed_emails?.length ?? 0) > 0) return true
  if ((rd.failed_docs?.length ?? 0) > 0) return true
  if ((rd.unprocessed_filenames?.length ?? 0) > 0) return true
  if ((rd.unprocessed_emails?.length ?? 0) > 0) return true
  if ((rd.unprocessed_document_ids?.length ?? 0) > 0) return true
  return false
}

export interface StartBulkApproveResponse {
  job_id: number
  status: string
  total_items: number
  message: string
}

export interface StartBulkUploadResponse {
  job_id: number
  status: string
  total_items: number
  message: string
}

export interface StartBulkEmailProcessResponse {
  job_id: number
  status: string
  total_items: number
  message: string
}

export interface StartBulkReprocessResponse {
  job_id: number
  status: string
  total_items: number
  message: string
}

export interface StartBulkAllocateSharedCostsResponse {
  job_id: number
  status: string
  total_items: number
  message: string
}

export interface StartBulkRefreshCategoriesResponse {
  job_id: number
  status: string
  total_items: number
  message: string
}

export async function startBulkApprove(documentIds: number[]): Promise<StartBulkApproveResponse> {
  const response = await api.post<StartBulkApproveResponse>('/jobs/bulk-approve', {
    document_ids: documentIds,
  })
  return response.data
}

export async function startBulkUpload(
  files: File[],
  options?: { emailRuleId?: number }
): Promise<StartBulkUploadResponse> {
  const filesData = await Promise.all(
    files.map(async (file) => {
      const arrayBuffer = await file.arrayBuffer()
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      )
      return {
        filename: file.name,
        content_base64: base64,
      }
    })
  )

  const response = await api.post<StartBulkUploadResponse>('/jobs/bulk-upload', {
    files: filesData,
    ...(options?.emailRuleId != null ? { email_rule_id: options.emailRuleId } : {}),
  })
  return response.data
}

export async function startBulkEmailProcess(
  emails: { gmail_id: string; inbox: string; email_rule_id?: number }[],
  options?: { include_previously_reviewed?: boolean }
): Promise<StartBulkEmailProcessResponse> {
  const response = await api.post<StartBulkEmailProcessResponse>('/jobs/bulk-process-emails', {
    emails,
    ...(options?.include_previously_reviewed
      ? { include_previously_reviewed: true }
      : {}),
  })
  return response.data
}

export async function startBulkReprocess(
  documentIds: number[],
  options?: { emailRuleId?: number }
): Promise<StartBulkReprocessResponse> {
  const response = await api.post<StartBulkReprocessResponse>('/jobs/bulk-reprocess', {
    document_ids: documentIds,
    ...(options?.emailRuleId != null ? { email_rule_id: options.emailRuleId } : {}),
  })
  return response.data
}

export async function startBulkAllocateSharedCosts(
  documentIds: number[]
): Promise<StartBulkAllocateSharedCostsResponse> {
  const response = await api.post<StartBulkAllocateSharedCostsResponse>(
    '/jobs/bulk-allocate-shared-costs',
    { document_ids: documentIds }
  )
  return response.data
}

export async function startBulkRefreshCategoriesJob(
  documentIds: number[]
): Promise<StartBulkRefreshCategoriesResponse> {
  const response = await api.post<StartBulkRefreshCategoriesResponse>(
    '/jobs/bulk-refresh-categories',
    { document_ids: documentIds }
  )
  return response.data
}

export async function getJobStatus(jobId: number): Promise<BackgroundJob> {
  const response = await api.get<BackgroundJob>(`/jobs/${jobId}`)
  return response.data
}

export async function getRecentJobs(limit: number = 10): Promise<BackgroundJob[]> {
  const response = await api.get<BackgroundJob[]>('/jobs/', { params: { limit } })
  return response.data
}

export function isFullDocumentJobPayload(doc: unknown): doc is Document {
  return (
    typeof doc === 'object' &&
    doc != null &&
    'id' in doc &&
    'line_items' in doc &&
    Array.isArray((doc as Document).line_items)
  )
}

/** Backend asyncio lock groups — only one job per group runs at a time. */
export function jobLockGroup(
  jobType: BackgroundJobType
): 'bulk_approve' | 'doc_review_extraction' | 'gcp_analysis' | null {
  if (jobType === 'bulk_approve') return 'bulk_approve'
  if (jobType === 'bulk_upload' || jobType === 'bulk_reprocess') return 'doc_review_extraction'
  if (jobType === 'gcp_cost_analysis') return 'gcp_analysis'
  return null
}

/** 1-based position among pending jobs in the same lock group, or null if not queued behind another job. */
export function pendingQueuePosition(job: BackgroundJob, allJobs: BackgroundJob[]): number | null {
  const group = jobLockGroup(job.job_type)
  if (job.status !== 'pending' || group == null) return null

  const peers = allJobs
    .filter(
      (j) =>
        jobLockGroup(j.job_type) === group &&
        (j.status === 'pending' || j.status === 'running')
    )
    .sort((a, b) => a.id - b.id)

  const hasRunner = peers.some((j) => j.status === 'running')
  if (!hasRunner) return null

  const pendingPeers = peers.filter((j) => j.status === 'pending')
  const idx = pendingPeers.findIndex((j) => j.id === job.id)
  if (idx < 0) return null
  return idx + 1
}
